/* Reaction.tsx generated by @compiled/babel-plugin v0.36.1 */
import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./Reaction.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ResourcedEmoji } from '@atlaskit/emoji';
import { createAndFireSafe, createReactionClickedEvent, createReactionFocusedEvent, createReactionHoveredEvent } from '../analytics';
import { Counter } from './Counter';
import { ReactionParticleEffect } from './ReactionParticleEffect';
import { ReactionTooltip } from './ReactionTooltip';
import { messages } from '../shared/i18n';
import { isLeftClick } from '../shared/utils';
import { RESOURCED_EMOJI_COMPACT_HEIGHT } from '../shared/constants';
import { ReactionButton } from './ReactionButton';
import { StaticReaction } from './StaticReaction';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { fg } from '@atlaskit/platform-feature-flags';
var styles = {
  container: "_kqswh2mm"
};
var emojiStyle = null;
var emojiNoReactionStyle = null;

/**
 * Test id for Reaction item wrapper div
 */
export var RENDER_REACTION_TESTID = 'render_reaction_wrapper';
/**
 * Render an emoji reaction button
 */
export var Reaction = function Reaction(_ref) {
  var emojiProvider = _ref.emojiProvider,
    onClick = _ref.onClick,
    reaction = _ref.reaction,
    _ref$onMouseEnter = _ref.onMouseEnter,
    onMouseEnter = _ref$onMouseEnter === void 0 ? function () {} : _ref$onMouseEnter,
    _ref$onFocused = _ref.onFocused,
    onFocused = _ref$onFocused === void 0 ? function () {} : _ref$onFocused,
    _ref$flash = _ref.flash,
    flash = _ref$flash === void 0 ? false : _ref$flash,
    _ref$showParticleEffe = _ref.showParticleEffect,
    showParticleEffect = _ref$showParticleEffe === void 0 ? false : _ref$showParticleEffe,
    _ref$showOpaqueBackgr = _ref.showOpaqueBackground,
    showOpaqueBackground = _ref$showOpaqueBackgr === void 0 ? false : _ref$showOpaqueBackgr,
    allowUserDialog = _ref.allowUserDialog,
    handleOpenReactionsDialog = _ref.handleOpenReactionsDialog,
    _ref$isViewOnly = _ref.isViewOnly,
    isViewOnly = _ref$isViewOnly === void 0 ? false : _ref$isViewOnly,
    showSubtleStyle = _ref.showSubtleStyle;
  var intl = useIntl();
  var hoverStart = useRef();
  var focusStart = useRef();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    emojiName = _useState2[0],
    setEmojiName = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isTooltipEnabled = _useState4[0],
    setIsTooltipEnabled = _useState4[1];
  var reacted = reaction.reacted;
  var emojiId = {
    id: reaction.emojiId,
    shortName: ''
  };

  // TODO: Extract the flow to a custom hook to retrieve emoji detailed description from an id using a custom hook. This will benefit a better optimization instead of the emojiProvider resolving everytime.
  // Also optimize in future version to fetch in batch several emojiIds
  useEffect(function () {
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var emojiResource, foundEmoji;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return Promise.resolve(emojiProvider);
          case 2:
            emojiResource = _context.sent;
            _context.next = 5;
            return emojiResource.findById(reaction.emojiId);
          case 5:
            foundEmoji = _context.sent;
            if (foundEmoji) {
              setEmojiName(foundEmoji.name);
            }
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  }, [emojiProvider, reaction.emojiId]);
  var handleClick = useCallback(function (event) {
    event.preventDefault();
    if (isLeftClick(event)) {
      var _reacted = reaction.reacted,
        _emojiId = reaction.emojiId;
      createAndFireSafe(createAnalyticsEvent, createReactionClickedEvent, !_reacted, _emojiId);
      onClick(reaction.emojiId, event);
    }
  }, [createAnalyticsEvent, reaction, onClick]);
  var handleMouseEnter = useCallback(function (event) {
    event.preventDefault();
    setIsTooltipEnabled(true);
    if (!reaction.users || !reaction.users.length) {
      focusStart.current = Date.now();
    }
    createAndFireSafe(createAnalyticsEvent, createReactionHoveredEvent, focusStart.current);
    onMouseEnter(reaction.emojiId, event);
  }, [createAnalyticsEvent, reaction, onMouseEnter]);
  var handleFocused = useCallback(function (event) {
    event.preventDefault();
    setIsTooltipEnabled(true);
    if (!reaction.users || !reaction.users.length) {
      hoverStart.current = Date.now();
    }
    createAndFireSafe(createAnalyticsEvent, createReactionFocusedEvent, hoverStart.current);
    onFocused(reaction.emojiId, event);
  }, [createAnalyticsEvent, reaction, onFocused]);
  var dismissTooltip = function dismissTooltip() {
    setIsTooltipEnabled(false);
  };
  var emojiAndCount = /*#__PURE__*/React.createElement(Inline, null, /*#__PURE__*/React.createElement("div", {
    className: ax(["_8hrz1nam _vwz41crf _ca0q1b66 _u5f31b66 _n3td1b66 _19bvu2gc", reaction.count === 0 && "_ca0q1b66 _u5f3v77o _n3td1b66 _19bv19bv"])
  }, /*#__PURE__*/React.createElement(ResourcedEmoji, _extends({
    emojiProvider: emojiProvider,
    emojiId: emojiId,
    fitToHeight: RESOURCED_EMOJI_COMPACT_HEIGHT
  }, reaction.emojiPath && fg('platform_optimistic_reaction_emoji') ? {
    optimistic: true,
    optimisticImageURL: reaction.emojiPath
  } : {}))), /*#__PURE__*/React.createElement(Counter, {
    value: reaction.count,
    highlight: !isViewOnly && reaction.reacted
  }));
  var reactionAriaLabel = intl.formatMessage(messages.reactWithEmoji, {
    emoji: emojiName
  });
  if (reaction.count) {
    reactionAriaLabel = intl.formatMessage(messages.reactWithEmojiAndCount, {
      emoji: emojiName,
      count: reaction.count
    });
  }
  return /*#__PURE__*/React.createElement(Box, {
    xcss: styles.container
  }, showParticleEffect && /*#__PURE__*/React.createElement(ReactionParticleEffect, {
    emojiId: emojiId,
    emojiProvider: emojiProvider
  }), /*#__PURE__*/React.createElement(ReactionTooltip, {
    emojiName: emojiName,
    reaction: reaction,
    isEnabled: isTooltipEnabled,
    allowUserDialog: allowUserDialog,
    handleOpenReactionsDialog: handleOpenReactionsDialog,
    dismissTooltip: dismissTooltip
  }, isViewOnly ? /*#__PURE__*/React.createElement(StaticReaction, {
    onMouseEnter: handleMouseEnter,
    onFocus: handleFocused,
    testId: RENDER_REACTION_TESTID,
    dataAttributes: {
      'data-emoji-id': reaction.emojiId
    }
  }, emojiAndCount) : /*#__PURE__*/React.createElement(ReactionButton, {
    onClick: handleClick,
    flash: flash,
    ariaLabel: reactionAriaLabel,
    ariaPressed: reacted,
    onMouseEnter: handleMouseEnter,
    onFocus: handleFocused,
    testId: RENDER_REACTION_TESTID,
    dataAttributes: {
      'data-emoji-id': reaction.emojiId,
      'data-emoji-button-id': reaction.emojiId
    },
    showOpaqueBackground: showOpaqueBackground,
    showSubtleStyle: showSubtleStyle,
    reacted: reacted
  }, emojiAndCount)));
};